import { useState } from "react";
import type { JSX } from "react";

import { ContextMenuSurface } from "../ui/ContextMenu";
import type { ContextMenuEntry } from "../ui/ContextMenu";

export interface WorkspaceMenuItem {
  readonly id: string;
  readonly label: string;
  readonly selected: boolean;
  readonly onSelect: () => void;
}

export interface ActiveCloudSession {
  readonly email: string;
  readonly onLogout: () => Promise<void>;
}

interface WorkspaceMenuProps {
  readonly items: readonly WorkspaceMenuItem[];
  readonly activeCloudSession: ActiveCloudSession | null;
  readonly onRequestPrivateWorkspace: () => void;
}

export function WorkspaceMenu({
  items,
  activeCloudSession,
  onRequestPrivateWorkspace,
}: WorkspaceMenuProps): JSX.Element {
  const [loggingOut, setLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState<string | null>(null);
  const menuItems: readonly ContextMenuEntry[] = [
    ...items.map((item): ContextMenuEntry => ({
      id: item.id,
      label: item.label,
      icon: item.selected ? "check" as const : undefined,
      role: "menuitemradio" as const,
      ariaChecked: item.selected,
    })),
    {
      id: "private_workspace",
      label: "Connect Private Workspace",
      role: "menuitem" as const,
    },
  ];

  const handleLogout = async (): Promise<void> => {
    if (activeCloudSession == null || loggingOut) return;
    setLoggingOut(true);
    setLogoutError(null);
    try {
      // @tested-by: tst_fe_workspace_menu_005
      await activeCloudSession.onLogout();
    } catch (error: unknown) {
      // @tested-by: tst_fe_workspace_menu_006
      setLogoutError(
        error instanceof Error ? error.message : "Magnis Cloud logout failed.",
      );
      setLoggingOut(false);
    }
  };

  return (
    <div
      className="absolute bottom-0 left-full z-30 ml-3"
      data-testid="workspace-menu"
      role="menu"
      aria-label="Account / Workspace"
    >
      <ContextMenuSurface
        items={menuItems}
        header="Account / Workspace"
        className="w-72"
        footer={
          activeCloudSession == null ? null : (
            <div
              className="mx-3 mt-1 border-t border-edge px-1 pb-1 pt-3"
              role="group"
              aria-label="Magnis Cloud session"
            >
              <p className="px-2 text-xs font-medium text-content-secondary">
                Signed in to Magnis Cloud
              </p>
              <p
                className="mt-1 truncate px-2 text-sm text-content"
                title={activeCloudSession.email}
              >
                {activeCloudSession.email}
              </p>
              <button
                type="button"
                className="mt-2 w-full rounded-lg px-2 py-1.5 text-left text-sm text-red-400 transition-colors hover:bg-surface-hover disabled:opacity-60"
                disabled={loggingOut}
                onClick={() => { void handleLogout(); }}
              >
                {loggingOut ? "Logging out..." : "Log out"}
              </button>
              {logoutError == null ? null : (
                <p role="alert" className="px-2 pt-1 text-xs text-red-400">
                  {logoutError}
                </p>
              )}
            </div>
          )
        }
        onSelect={(itemId) => {
          if (itemId === "private_workspace") {
            onRequestPrivateWorkspace();
            return;
          }
          const item = items.find((entry) => entry.id === itemId);
          item?.onSelect();
        }}
      />
    </div>
  );
}
